import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  TextField,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tabs,
  Tab,
  Card,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Save,
  Edit,
  PermIdentity,
  VpnKey,
  AccountBox,
  ChevronRight
} from '@mui/icons-material';
import ProfilePicture from '../components/ProfilePicture';
import TelegramLinking from '../components/TelegramLinking';
import TelegramStatus from '../components/TelegramStatus';
import api from '../config/api';

const Profile = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone_number: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Check for query params (for any success messages or tab navigation)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('tab') === 'connected') {
      setActiveTab(2);
    }
    if (params.get('discord_linked') === 'true') {
      setSuccess(t('profile.discordLinked'));
    }
    if (params.get('tab') || params.get('discord_linked')) {
      navigate('/profile', { replace: true });
    }
  }, [location, navigate, t]);

  // Fetch user profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        // Get user from localStorage first for faster display
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (storedUser && storedUser.email) {
          setUser(storedUser);
          setFormData({
            name: storedUser.name || '',
            email: storedUser.email || '',
            phone_number: storedUser.phone_number || '',
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
          setLoading(false); // Stop loading when we have data from localStorage
        }

        // Then fetch the latest from the server
        try {
          const response = await api.get('/auth/profile');
          setUser(response.data);
          setFormData({
            name: response.data.name || '',
            email: response.data.email || '',
            phone_number: response.data.phone_number || '',
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });

          // Update localStorage with fresh data
          localStorage.setItem('user', JSON.stringify(response.data));
          setLoading(false);
        } catch (err) {
          // Only set error if we don't already have user data from localStorage
          if (!user) {
            setError(t('profile.failedToFetch'));
            setLoading(false);
          }
        }
      } catch (err) {
        setError(t('profile.failedToFetch'));
        setLoading(false);
      }
    };

    fetchProfile();
  }, [navigate, user, t]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleProfilePictureUpdate = (newPictureData) => {
    if (!newPictureData) {
      // Handle profile picture removal
      setUser({ ...user, profile_picture_url: null });
      setSuccess(t('profile.profilePictureRemoved'));
      return;
    }

    // Handle profile picture update
    setUser({ ...user, profile_picture_url: newPictureData });
    setSuccess(t('profile.profilePictureUpdated'));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Basic field validation
    if (!formData.name || !formData.email) {
      setError(t('profile.nameEmailRequired'));
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(t('profile.invalidEmail'));
      return;
    }

    // Validate password fields if attempting to change password
    if (formData.newPassword || formData.confirmPassword || formData.currentPassword) {
      if (formData.newPassword !== formData.confirmPassword) {
        setError(t('profile.passwordsDoNotMatch'));
        return;
      }
      if (!formData.currentPassword) {
        setError(t('profile.currentPasswordRequired'));
        return;
      }

      // Check password strength
      if (formData.newPassword.length < 8) {
        setError(t('profile.passwordMinLength'));
        return;
      }
    }

    // Check if email is being changed
    const isEmailChanged = user.email !== formData.email;

    // Prepare the data to submit
    const updateData = {
      name: formData.name,
      email: formData.email,
      phone_number: formData.phone_number || null
    };

    // Add password fields if changing password
    if (formData.newPassword && formData.currentPassword) {
      updateData.currentPassword = formData.currentPassword;
      updateData.newPassword = formData.newPassword;
    }

    // If email is changing, ask for confirmation
    if (isEmailChanged) {
      setPendingSubmitData(updateData);
      setEmailDialogOpen(true);
      return;
    }

    // Otherwise, proceed with the update
    submitProfileUpdate(updateData);
  };

  // Submit profile updates
  const submitProfileUpdate = async (updateData) => {
    try {
      const response = await api.put('/auth/profile', updateData);

      // Check if the server returned a new token (email change)
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        // Display special message for email change
        setSuccess(t('profile.profileUpdatedEmailChanged'));
      } else {
        setSuccess(t('profile.profileUpdated'));
      }

      // Update local user data
      const updatedUser = { ...response.data };
      delete updatedUser.token; // Remove token from user object if it exists

      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));

      // Dispatch custom event to notify other components of user data change
      window.dispatchEvent(new Event('userDataChanged'));

      setEditing(false);

      // Clear password fields
      setFormData({
        ...formData,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      setError(err.response?.data?.error || t('profile.failedToUpdate'));
    }
  };

  // Handle dialog responses
  const handleEmailDialogClose = () => {
    setEmailDialogOpen(false);
    setPendingSubmitData(null);
  };

  const handleEmailDialogConfirm = () => {
    if (pendingSubmitData) {
      submitProfileUpdate(pendingSubmitData);
    }
    setEmailDialogOpen(false);
    setPendingSubmitData(null);
  };

  // Add the Telegram account handlers
  const handleTelegramLinkSuccess = (data) => {
    // Update user object with the new telegram_id
    setUser(prevUser => ({
      ...prevUser,
      telegram_id: data.telegram_id
    }));

    // Update user in localStorage
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    storedUser.telegram_id = data.telegram_id;
    localStorage.setItem('user', JSON.stringify(storedUser));

    setSuccess(t('profile.telegramLinked'));
  };

  const handleTelegramUnlink = () => {
    // Update user object to remove telegram_id
    setUser(prevUser => ({
      ...prevUser,
      telegram_id: null
    }));

    // Update user in localStorage
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    delete storedUser.telegram_id;
    localStorage.setItem('user', JSON.stringify(storedUser));

    setSuccess(t('profile.telegramUnlinked'));
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Main profile content
  const renderPersonalInfoTab = () => (
    <Box component="form" onSubmit={handleSubmit} sx={{ pt: 3 }}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Card sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            height: '100%'
          }}>
            <ProfilePicture
              currentUrl={user.profile_picture_url}
              onUpdate={handleProfilePictureUpdate}
              userId={user.email}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              {t('profile.clickCameraToUpdate')}
            </Typography>

            <Box sx={{ mt: 3, width: '100%' }}>
              <Typography variant="h6" gutterBottom>{t('profile.accountInfo')}</Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">{t('profile.accountId')}</Typography>
                <Typography variant="body1" fontWeight="medium">{user.id || '—'}</Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">{t('profile.joined')}</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">{t('profile.lastUpdated')}</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {user.updated_at ? new Date(user.updated_at).toLocaleDateString() : '—'}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card sx={{ p: 4, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5">{t('profile.personalInformation')}</Typography>
              <Button
                color={editing ? "primary" : "secondary"}
                variant={editing ? "contained" : "outlined"}
                startIcon={editing ? <Save /> : <Edit />}
                onClick={() => editing ? handleSubmit() : setEditing(true)}
              >
                {editing ? t('profile.saveChanges') : t('profile.editProfile')}
              </Button>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('profile.fullName')}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={!editing}
                  required
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('auth.emailLabel')}
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={!editing}
                  required
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('profile.phoneNumber')}
                  name="phone_number"
                  value={formData.phone_number || ''}
                  onChange={handleChange}
                  disabled={!editing}
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderSecurityTab = () => (
    <Box component="form" onSubmit={handleSubmit} sx={{ pt: 3 }}>
      <Card sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          {t('profile.securitySettings')}
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('profile.currentPassword')}
              name="currentPassword"
              type="password"
              value={formData.currentPassword}
              onChange={handleChange}
              variant="outlined"
              required={formData.newPassword || formData.confirmPassword}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('profile.newPassword')}
              name="newPassword"
              type="password"
              value={formData.newPassword}
              onChange={handleChange}
              variant="outlined"
              helperText={t('profile.minChars')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('profile.confirmNewPassword')}
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              variant="outlined"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            startIcon={<Save />}
          >
            {t('profile.updatePassword')}
          </Button>
        </Box>
      </Card>
    </Box>
  );

  const renderConnectedAccountsTab = () => (
    <Box sx={{ pt: 3 }}>
      <Card sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          {t('profile.connectedAccounts')}
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Telegram
          </Typography>

          {user?.telegram_id ? (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: '#0088cc', mr: 2 }}>T</Avatar>
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    Telegram
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID: {user.telegram_id}
                  </Typography>
                </Box>
              </Box>
              <Chip
                label={t('common.connected')}
                color="success"
                size="small"
                onDelete={() => handleTelegramUnlink()}
              />
            </Box>
          ) : (
            <TelegramLinking
              onSuccess={handleTelegramLinkSuccess}
              onError={(message) => setError(message)}
            />
          )}
        </Box>
      </Card>
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, pb: 6 }}>
      {/* Header Card */}
      <Card sx={{ mb: 3, p: 4, background: 'linear-gradient(145deg, #2196f3 0%, #1976d2 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Avatar
            src={user.profile_picture_url}
            sx={{ width: 64, height: 64, border: '3px solid white' }}
          />
          <Box>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 600 }}>
              {user.name || t('profile.userProfile')}
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              {user.email}
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Notification area */}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

      {/* Tabs navigation */}
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant={isMobile ? "fullWidth" : "standard"}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab icon={<PermIdentity />} label={t('profile.personalInfo')} iconPosition="start" />
            <Tab icon={<VpnKey />} label={t('profile.security')} iconPosition="start" />
            <Tab icon={<AccountBox />} label={t('profile.connectedAccounts')} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Tab contents */}
        {activeTab === 0 && renderPersonalInfoTab()}
        {activeTab === 1 && renderSecurityTab()}
        {activeTab === 2 && renderConnectedAccountsTab()}
      </Box>

      {/* Email change confirmation dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={handleEmailDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('profile.confirmEmailChange')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description" dangerouslySetInnerHTML={{ __html: t('profile.emailChangeWarning', { oldEmail: user?.email, newEmail: formData.email }) }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEmailDialogClose} color="primary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleEmailDialogConfirm} color="primary" autoFocus>
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Profile;